import { type AssignableRole, aiLimitAfterTemplate, aiMonthlyLimitSchema } from '@fos/shared/contracts/team';
import type { PermissionTemplate, Role } from '@fos/shared/enums';
import { ROLE_LABELS, TEAM_ROLE_DESCRIPTIONS } from '@fos/shared/labels';
import { appErrorCode, errorMessage } from '../../../lib/api';
import { formatMoneyAuto } from '../../../lib/format';
import { toast } from '../../../ui/Toast';
import type { ChoiceOption } from './components/ChoiceCards';

/** Pomocnicze funkcje ekranów Ustawienia → Zespół (czyste, łatwe do testów). */

/** Kolejność kart roli jak w Figmie: Użytkownik, Manager, Admin. */
export const ROLE_CHOICES: ChoiceOption<AssignableRole>[] = (['user', 'manager', 'admin'] as const).map((role) => ({
  value: role,
  label: ROLE_LABELS[role],
  description: TEAM_ROLE_DESCRIPTIONS[role],
}));

/**
 * Limit AI z pola tekstowego: „30”, „30,50”, „ 30.5 ” → string do API; puste → null; błąd → komunikat.
 */
export function parseAiLimit(text: string): { value: string | null; error: string | null } {
  const trimmed = text.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return { value: null, error: null };
  const parsed = aiMonthlyLimitSchema.safeParse(trimmed);
  if (!parsed.success) return { value: null, error: 'Podaj kwotę w zł, np. 30 albo 30,50' };
  return { value: parsed.data, error: null };
}

/** Wartość z API („30.00”) do pola tekstowego („30”, „30,50”). */
export function aiLimitToInput(value: string | null): string {
  if (!value) return '';
  const [whole, fraction = ''] = value.split('.');
  const trimmedFraction = fraction.replace(/0+$/, '');
  return trimmedFraction ? `${whole},${trimmedFraction.padEnd(2, '0')}` : (whole ?? '');
}

/**
 * Pole „Limit AI” po zmianie szablonu albo roli (szablon działa tylko przy roli Użytkownik): szablon Partner wpisuje
 * 0 zł (AI we własnym workspace'ie), odejście od niego czyści limit nadal równy domyślnemu; kwota wpisana ręcznie
 * (i tekst z błędem) zostaje. Ta sama reguła co `team.applyTemplate` (`aiLimitAfterTemplate`).
 */
export function aiLimitInputAfterTemplate(
  prev: PermissionTemplate | null,
  next: PermissionTemplate | null,
  text: string,
): string {
  const current = parseAiLimit(text);
  if (current.error) return text;
  const result = aiLimitAfterTemplate(prev, next, current.value);
  return result === current.value ? text : aiLimitToInput(result);
}

/** „200 zł” w tabeli (mono); brak limitu = „—”. */
export function formatAiLimit(value: string | null): string {
  return value ? formatMoneyAuto(value) : '—';
}

/** Inicjały z adresu email (zaproszenie: osoba nie ma jeszcze imienia). */
export function initialsFromEmail(email: string): string {
  const local = email.split('@')[0] ?? '';
  const parts = local.split(/[._-]+/).filter(Boolean);
  const letters = parts.length >= 2 ? `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}` : local.slice(0, 2);
  return letters.toUpperCase() || '?';
}

export function canEditRole(target: { role: Role; isSelf: boolean }, actorRole: Role | null): boolean {
  if (target.isSelf) return false;
  if (target.role === 'owner') return false;
  return actorRole === 'owner' || actorRole === 'admin';
}

/** Błąd mutacji jako toast (anulowane potwierdzenie hasłem nie jest błędem do pokazania). */
export function toastMutationError(err: unknown): void {
  if (appErrorCode(err) === 'STEP_UP_REQUIRED') return;
  toast.error(errorMessage(err));
}
